import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

// "Add Bonus Tiffin" — sets an ABSOLUTE count of extra tiffins on top of the
// plan's normal quantity (not a live +/- apply-immediately control). Saving
// pushes the subscription's end_date out automatically (same deliver/skip
// math as everything else) since bonus_tiffins just raises the target count.
export function BonusTiffinDialog({ open, onOpenChange, subscription, onSaved }) {
  const [count, setCount] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setCount(subscription?.bonus_tiffins || 0);
  }, [open, subscription]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.post(`/subscriptions/${subscription.id}/bonus-tiffins`, { bonus_tiffins: count });
      toast.success(count === 0 ? "Bonus tiffins removed" : `Set to ${count} bonus tiffin${count === 1 ? "" : "s"}`);
      onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update bonus tiffins."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bonus-tiffin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gift size={18} strokeWidth={1.5} /> Bonus Tiffins</DialogTitle>
          <DialogDescription>
            Extra tiffins given on top of this subscription's plan quantity. Extends the end date automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-6 py-6">
          <Button
            type="button" variant="outline" size="icon"
            onClick={() => setCount((c) => Math.max(0, c - 1))}
            disabled={count <= 0}
            data-testid="bonus-tiffin-decrement"
          >
            <Minus size={16} strokeWidth={2} />
          </Button>
          <span className="w-16 text-center font-heading text-4xl font-semibold text-text-main" data-testid="bonus-tiffin-count">
            {count}
          </span>
          <Button
            type="button" variant="outline" size="icon"
            onClick={() => setCount((c) => Math.min(200, c + 1))}
            data-testid="bonus-tiffin-increment"
          >
            <Plus size={16} strokeWidth={2} />
          </Button>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="bonus-tiffin-cancel-button">
            Cancel
          </Button>
          <Button type="button" variant="accent" onClick={handleSave} disabled={saving} data-testid="bonus-tiffin-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
